<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    :title="mode === 'R' || mode === 'I' ? 'QoS設定' : '確認'"
  >
    <template v-slot:body="{ ok, cancel }">
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container>
            <!-- 参照モード -->
            <v-row v-if="mode === 'R'">
              <v-col class="d-flex flex-row justify-end">
                <v-btn
                  v-permission.config
                  color="primary"
                  @click="showModifyModal"
                  >変更</v-btn
                >
              </v-col>
            </v-row>
            <!-- 確認モード -->
            <v-row class="mb-2" v-else-if="mode === 'C'">
              <label v-if="isNew" class="font-weight-bold"
                >こちらの内容でQoSを設定します。よろしいですか？</label
              >
              <label v-else class="font-weight-bold"
                >こちらの内容でQoS設定を変更します。よろしいですか？</label
              >
            </v-row>

            <!-- 未登録の場合 -->
            <template v-if="qosDetail.qosType === 'NONE'">
              <v-row>
                <v-col cols="12">
                  <app-label-group label="QoS方式" value="設定無し" />
                </v-col>
              </v-row>
            </template>

            <!-- QoS設定がある場合（QoS方式：絶対優先方式） -->
            <template v-if="qosDetail.qosType === 'ABSOLUTE'">
              <v-row>
                <v-col cols="12">
                  <app-label-group label="QoS方式" value="絶対優先方式" />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <app-label-group
                    label="優先度参照先"
                    :value="
                      $filter.enumTo(
                        qosDetail.priorityTarget,
                        'qos_PriorityTarget'
                      )
                    "
                  />
                </v-col>
              </v-row>
              <h3 class="mt-3">優先度割当</h3>
              <v-table density="compact" class="table-bordered mt-3">
                <thead>
                  <tr>
                    <th colspan="2" class="text-center">CoS/IP Precedence</th>
                    <th v-for="index in 8" :key="index" class="text-center">
                      {{ index - 1 }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <th :rowspan="5">優先度</th>
                  <tr
                    v-for="(rowName, rowNameIndex) in absolutePriorityRowNames"
                    :key="rowNameIndex"
                  >
                    <th class="text-left table-light-color pl-1">
                      {{ rowName.value }}
                    </th>
                    <td
                      v-for="(
                        propName, propNameIndex
                      ) in absolutePriorityPropNames"
                      :key="propNameIndex"
                      class="text-center"
                    >
                      {{
                        qosDetail.qosAbsolute![propName] === rowName.key
                          ? "●"
                          : "○"
                      }}
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </template>

            <!-- QoS設定がある場合（QoS方式：帯域制御方式） -->
            <template v-if="qosDetail.qosType === 'BANDWIDTH'">
              <v-row>
                <v-col cols="12">
                  <app-label-group label="QoS方式" value="帯域制御方式" />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <app-label-group
                    label="優先度参照先"
                    :value="
                      $filter.enumTo(
                        qosDetail.priorityTarget,
                        'qos_PriorityTarget'
                      )
                    "
                  />
                </v-col>
              </v-row>
              <h3 class="mt-3">優先度割当</h3>
              <v-table density="compact" class="table-bordered mt-3">
                <thead>
                  <tr>
                    <th colspan="2" rowspan="2"></th>
                    <th colspan="10" class="text-center">CoS/IP Precedence</th>
                  </tr>
                  <tr>
                    <th class="text-right">
                      {{
                        $filter.enumTo(
                          qosDetail.qosBandwidth!.priorityType1,
                          "qos_PriorityType"
                        )
                      }}
                    </th>
                    <th class="text-right">
                      {{
                        $filter.enumTo(
                          qosDetail.qosBandwidth!.priorityType2,
                          "qos_PriorityType"
                        )
                      }}
                    </th>
                    <th class="text-right">
                      {{
                        $filter.enumTo(
                          qosDetail.qosBandwidth!.priorityType3,
                          "qos_PriorityType"
                        )
                      }}
                    </th>
                    <th class="text-right">ベストエフォート</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(rowName, rowNameIndex) in bandwidthRowNames"
                    :key="rowNameIndex"
                  >
                    <template v-if="rowNameIndex % 2 === 0">
                      <th rowspan="2">{{ rowName.value }}</th>
                      <th>割当帯域</th>
                      <td
                        v-for="(propName, propNameIndex) in bandwidthPropNames"
                        :key="propNameIndex"
                        class="text-right"
                      >
                        {{ getBandwidthValue(rowName.key, propName) }}
                      </td>
                    </template>
                    <template v-else>
                      <th>最優先</th>
                      <td class="text-right">
                        {{ isType1TopPriority(rowName.key) }}
                      </td>
                      <td colspan="3"></td>
                    </template>
                  </tr>
                </tbody>
              </v-table>
            </template>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <!-- FOOTER -->
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <!-- 参照モード -->
          <template v-if="mode === 'R' || mode === 'I'" v-slot:append>
            <v-btn @click="cancel" variant="outlined" color="primary"
              >戻る</v-btn
            >
          </template>
          <!-- 参照モード -->
          <template v-else v-slot:append>
            <v-btn @click="ok" variant="flat" color="primary">はい</v-btn>
            <v-btn @click="cancel" variant="outlined" color="primary"
              >いいえ</v-btn
            >
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { QosDetail } from "@/apis/QosApi";
import { VueFinalModal } from "vue-final-modal";
import QosSettingEdit from "@/modals/qos/QosSettingEdit.vue";

export default defineComponent({
  name: "QosSettingViewRC",
  props: {
    // 変更画面で使用
    vpnVnCode: {
      type: String as PropType<string>,
      required: false,
      default: "",
    },
    // 変更画面で使用
    vnName: {
      type: String as PropType<string>,
      required: false,
      default: "",
    },
    // R or C or I (Reference or Confirm or Information)
    mode: {
      type: String as PropType<string>,
      required: false,
      default: "R",
    },
    isNew: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    qosDetail: {
      type: Object as PropType<QosDetail> | null,
      required: true,
    },
  },
  data() {
    return {
      absolutePriorityRowNames: [
        { key: "HIGH", value: "High" },
        { key: "MEDIUM", value: "Medium" },
        { key: "NORMAL", value: "Normal" },
        { key: "LOW", value: "Low" },
      ],
      absolutePriorityPropNames: [
        "priority0",
        "priority1",
        "priority2",
        "priority3",
        "priority4",
        "priority5",
        "priority6",
        "priority7",
      ] as Array<keyof QosDetail["qosAbsolute"]>,
      bandwidthRowNames: [
        { key: "PATTERN_A", value: "パターンA" },
        { key: "PATTERN_A", value: "" },
        { key: "PATTERN_B", value: "パターンB" },
        { key: "PATTERN_B", value: "" },
        { key: "PATTERN_C", value: "パターンC" },
        { key: "PATTERN_C", value: "" },
        { key: "PATTERN_D", value: "パターンD" },
        { key: "PATTERN_D", value: "" },
        { key: "PATTERN_E", value: "パターンE" },
        { key: "PATTERN_E", value: "" },
      ],
      bandwidthPropNames: [
        "priorityType1Value",
        "priorityType2Value",
        "priorityType3Value",
        "bestEffort",
      ],
    };
  },
  methods: {
    async showModifyModal() {
      await this.$modal.show(QosSettingEdit, {
        vpnVnCode: this.vpnVnCode,
        vnName: this.vnName,
        isNew: this.isNew,
        qosDetail: this.qosDetail,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /**
     * 帯域制御方式の値を取得
     * @param patternName パターン名
     * @param propName プロパティ名
     * */
    getBandwidthValue: function (patternName: string, propName: string) {
      const item = this.qosDetail.qosBandwidth?.qosBandwidthList?.find(
        (e) => e.patternName === patternName
      );
      if (item) {
        if (propName === "bestEffort") {
          return (item.priorityType1Value || 0) +
            (item.priorityType2Value || 0) +
            (item.priorityType3Value || 0)
            ? `${
                100 -
                ((item.priorityType1Value || 0) +
                  (item.priorityType2Value || 0) +
                  (item.priorityType3Value || 0))
              }%`
            : "-";
        } else {
          switch (propName) {
            case "priorityType1Value":
              return item.priorityType1Value
                ? `${item.priorityType1Value}%`
                : "-";
            case "priorityType2Value":
              return item.priorityType2Value
                ? `${item.priorityType2Value}%`
                : "-";
            case "priorityType3Value":
              return item.priorityType3Value
                ? `${item.priorityType3Value}%`
                : "-";
          }
        }
      } else {
        return "-";
      }
    },
    /**
     * 帯域制御方式の最優先設定フラグを取得
     * @param patternName パターン名
     * */
    isType1TopPriority(patternName: string) {
      const item = this.qosDetail.qosBandwidth?.qosBandwidthList?.find(
        (e) => e.patternName === patternName
      );
      if (item) {
        return item.isType1TopPriority ? "ON" : "OFF";
      } else {
        return "-";
      }
    },
  },
});
</script>

<style lang="scss" scoped>
th {
  font-weight: bold !important;
  background-color: #e9ecef;
  padding: 4px !important;
}
</style>
